import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { CalibrationReportFilters } from '@/types/calibration';
import type { Company } from '@/types/directory/Company';

type FilterAttribute = { id?: string };

const toIds = (items: FilterAttribute[]): string[] =>
    items.map((item) => item.id).filter((id): id is string => !!id);

const ifNotEmpty = (ids: string[]): string[] | undefined => (ids.length > 0 ? ids : undefined);

const belongsTo = (ids: Set<string>, attribute: FilterAttribute): boolean =>
    !!attribute.id && ids.has(attribute.id);

export interface ToReportFiltersInput {
    /** What the toolbar's company-attribute dropdown currently has selected. */
    filters: SelectedFilters;
    /** The current company, read for its `ageGroups`/`tenures` lists; `undefined` until loaded. */
    company: Company | undefined;
}

/**
 * Converts the toolbar's `SelectedFilters` into the calibration report's own query filters. An
 * empty bucket is omitted entirely rather than sent as `[]`, matching how every array filter on
 * this contract means "no restriction" only when absent.
 *
 * `useCompanyAttributesFilters` has dedicated buckets only for departments, jobTitles,
 * companyAddresses, employeeLevels and managers (its `standardKeys` lists); every other company
 * attribute a user can pick — age group and tenure included — lands in the generic
 * `customAttributes` bucket. The report's contract has dedicated `ageGroupIds`/`tenureIds` params,
 * so `customAttributes` is re-split here against the company's own `ageGroups`/`tenures` lists,
 * the same rule the competency-matrix report applies. Whatever matches neither is a genuine
 * custom attribute and goes to `customAttributeValueIds`.
 *
 * Scoped to this report rather than pushed into the shared hook: `customAttributes` is read by
 * several other pages (`DefineAudiencePage`, `SelectRevieweePage`, `DashboardPage`, …), each
 * mapping it straight to its own `customAttributeIds`, and moving age group/tenure out of that
 * bucket would silently change what they send.
 */
export const toReportFilters = ({
    filters,
    company,
}: ToReportFiltersInput): CalibrationReportFilters => {
    const companyAgeGroupIds = new Set(toIds(company?.ageGroups ?? []));
    const companyTenureIds = new Set(toIds(company?.tenures ?? []));

    const selectedAgeGroups = filters.customAttributes.filter((attribute) =>
        belongsTo(companyAgeGroupIds, attribute),
    );
    const selectedTenures = filters.customAttributes.filter((attribute) =>
        belongsTo(companyTenureIds, attribute),
    );
    const selectedCustomAttributes = filters.customAttributes.filter(
        (attribute) =>
            !belongsTo(companyAgeGroupIds, attribute) && !belongsTo(companyTenureIds, attribute),
    );

    return {
        departmentIds: ifNotEmpty(toIds(filters.departments)),
        jobTitleIds: ifNotEmpty(toIds(filters.jobTitles)),
        employeeLevelIds: ifNotEmpty(toIds(filters.employeeLevels)),
        companyAddressIds: ifNotEmpty(toIds(filters.companyAddresses)),
        managerIds: ifNotEmpty(toIds(filters.managers)),
        ageGroupIds: ifNotEmpty(toIds(selectedAgeGroups)),
        tenureIds: ifNotEmpty(toIds(selectedTenures)),
        customAttributeValueIds: ifNotEmpty(toIds(selectedCustomAttributes)),
    };
};
